import { CloseRounded } from '@mui/icons-material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import { ITruckGroupListDTO } from 'app/shared/model/truck-group.model';
import { Loading } from 'app/shared/util/loading';
import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';

interface ITruckGroupSelectionModalProps {
  handleSave: (selectedGroupIds: number[]) => void;
  toggleModal: () => void;
  initialSelectedGroupIds: number[];
  open: boolean;
  companyId?: number;
}

export const TruckGroupSelectionModal = ({ handleSave, toggleModal, initialSelectedGroupIds, open, companyId }: ITruckGroupSelectionModalProps) => {
  const theme = useTheme();

  const [groupList, setGroupList] = useState<ITruckGroupListDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<Set<number>>(new Set(initialSelectedGroupIds || []));
  const [filterValue, setFilterValue] = useState('');

  useEffect(() => {
    const fetchGroups = async () => {
      setLoading(true);
      try {
        const companyParam = companyId ? `&companyId=${companyId}` : '';
        const response = await axios.get<ITruckGroupListDTO[]>(`api/truck-groups?page=0&size=1000&sort=name,asc${companyParam}`);
        setGroupList(response.data);
      } catch (error) {
        console.error('Error fetching alert groups:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, [companyId]);

  const filteredGroups = useMemo(() => {
    if (!filterValue) return groupList;
    return groupList.filter(g => (g.name || '').toLowerCase().includes(filterValue.toLowerCase()));
  }, [groupList, filterValue]);

  const handleToggle = (id: number) => {
    setSelectedGroups(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSaveClick = () => {
    handleSave(Array.from(selectedGroups));
    toggleModal();
  };

  const allSelected = filteredGroups.length > 0 && filteredGroups.every(g => selectedGroups.has(g.id));
  const someSelected = filteredGroups.some(g => selectedGroups.has(g.id));

  const handleSelectAll = () => {
    const allIds = filteredGroups.map(g => g.id);
    const allSel = allIds.every(id => selectedGroups.has(id));
    setSelectedGroups(prev => {
      const next = new Set(prev);
      allIds.forEach(id => (allSel ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={toggleModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '15px', background: theme.palette.primary.main } }}
        sx={{
          boxShadow:
            'rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px',
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 500, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            Grupos
            <Typography component="span" variant="caption" sx={{ opacity: 0.7, fontWeight: 400 }}>
              ({selectedGroups.size}/{filteredGroups.length} selecionados)
            </Typography>
          </Typography>
          <IconButton onClick={toggleModal}>
            <CloseRounded color="secondary" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {!loading && groupList.length > 0 && (
            <>
              <Box display="flex" alignItems="center" mt={1} mb={2}>
                <TextField
                  fullWidth
                  size="small"
                  value={filterValue}
                  onChange={e => setFilterValue(e.target.value)}
                  placeholder="Buscar por nome do grupo"
                  InputProps={{
                    sx: { borderRadius: '8px', p: '0 8px 2px 0' },
                    endAdornment: (
                      <InputAdornment position="end">
                        {!filterValue ? (
                          <SearchOutlinedIcon color="action" />
                        ) : (
                          <IconButton size="small" onClick={() => setFilterValue('')}>
                            <CloseOutlinedIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box>
                <FormControlLabel
                  control={<Checkbox checked={allSelected} indeterminate={someSelected && !allSelected} onChange={handleSelectAll} color="secondary" />}
                  label="Selecionar Todos"
                />
              </Box>
            </>
          )}
          <Box sx={{ mt: 1 }}>
            {loading ? (
              <Loading />
            ) : filteredGroups.length > 0 ? (
              filteredGroups.map(group => (
                <Box
                  key={group.id}
                  display="flex"
                  alignItems="center"
                  onClick={() => handleToggle(group.id)}
                  style={{ cursor: 'pointer' }}
                  sx={{ py: 0.75, borderBottom: `1px solid rgba(255,255,255,0.06)`, '&:last-child': { borderBottom: 'none' } }}
                >
                  <Checkbox checked={selectedGroups.has(group.id)} color="secondary" />
                  <Box ml={1}>
                    <Typography variant="subtitle2">{group.name}</Typography>
                    {group.isDefault && (
                      <Typography variant="caption" color="text.secondary">
                        Grupo Padrão
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))
            ) : (
              <Box display="flex" justifyContent="center" mt={2} mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Nenhum grupo encontrado.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '8px 24px', my: 2 }}>
          <Button onClick={toggleModal} disableRipple variant="text" sx={{ color: 'text.primary' }}>
            Cancelar
          </Button>
          <Button onClick={handleSaveClick} disableRipple variant="contained" color="secondary">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};
